/**
 * PageLayoutCalculator - Utilities for page-relative Y-position calculations
 *
 * Extracted from EdgeDocsCanvas.V3.tsx as part of Phase 3 Step 8.
 *
 * When rendering multi-page documents, lines have absolute Y positions from 
 * the document start. For each page to render correctly, we need to adjust
 * these positions to be relative to the page's top margin.
 */

/**
 * Base interface for line with Y-position info
 */
export interface LineWithYPosition {
  baseline_y_fp: number;
  ascent_fp?: number;
}

/**
 * Base interface for caret entry with Y-position
 */
export interface CaretEntryWithYPosition {
  y_fp: number;
}

/**
 * Page margins in fixed-point units
 */
export interface PageMarginsRef {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

/**
 * Options for calculating page Y offset
 */
export interface PageYOffsetOptions {
  /** Index of the current page (0-based) */
  pageIndex: number;
  /** Top margin in fixed-point units */
  topMarginFp: number;
  /** Enable console logging */
  enableLogging?: boolean;
}

/**
 * Result of page Y offset calculation
 */
export interface PageYOffsetResult {
  /** The Y offset to subtract from positions */
  pageYOffset: number;
  /** Whether adjustment is needed */
  needsAdjustment: boolean;
  /** First line's original baseline (for logging) */
  originalFirstBaseline: number;
  /** First line's expected baseline after adjustment */
  expectedFirstBaseline: number;
}

/**
 * Result of adjusting lines for page-relative positioning
 */
export interface AdjustedLinesResult<T extends LineWithYPosition> {
  /** Adjusted lines with corrected Y positions */
  lines: T[];
  /** The Y offset that was applied */
  pageYOffset: number;
  /** Number of lines adjusted */
  lineCount: number;
}

/**
 * Calculates the Y offset needed for page-relative positioning
 *
 * For pages > 0, lines have absolute Y positions from document start.
 * This function calculates the offset needed to position the first line
 * at the top margin.
 *
 * @param lines - Lines on the current page
 * @param options - Calculation options
 * @returns Offset result with adjustment details
 *
 * @example
 * ```typescript
 * const result = calculatePageYOffset(pageLines, {
 *   pageIndex: 1,
 *   topMarginFp: 72 * 64, // 72pt = 1 inch in fixed-point
 *   enableLogging: true
 * });
 * 
 * if (result.needsAdjustment) {
 *   // Apply result.pageYOffset to all Y positions
 * }
 * ```
 */
export function calculatePageYOffset<T extends LineWithYPosition>(
  lines: T[],
  options: PageYOffsetOptions
): PageYOffsetResult {
  const { pageIndex, topMarginFp, enableLogging = false } = options;

  // No adjustment needed for page 0 or empty pages
  if (pageIndex === 0 || lines.length === 0) {
    return {
      pageYOffset: 0,
      needsAdjustment: false,
      originalFirstBaseline: 0,
      expectedFirstBaseline: 0
    };
  }

  const firstLine = lines[0];
  const firstLineBaseline = firstLine.baseline_y_fp ?? 0;
  const firstLineAscent = firstLine.ascent_fp ?? 0;
  
  // Calculate expected baseline: top margin + first line's ascent
  const expectedFirstLineBaseline = topMarginFp + firstLineAscent;
  
  // Calculate offset to apply
  const pageYOffset = firstLineBaseline - expectedFirstLineBaseline;
  const needsAdjustment = pageYOffset !== 0;

  if (enableLogging && needsAdjustment) {
    console.log(`📐 [PageLayoutCalculator] Page ${pageIndex}: Calculated Y offset ${pageYOffset / 64}px`, {
      originalFirstBaseline: firstLineBaseline / 64,
      expectedFirstBaseline: expectedFirstLineBaseline / 64,
      topMargin: topMarginFp / 64,
      firstLineAscent: firstLineAscent / 64
    });
  }

  return {
    pageYOffset,
    needsAdjustment,
    originalFirstBaseline: firstLineBaseline,
    expectedFirstBaseline: expectedFirstLineBaseline
  };
}

/**
 * Adjusts line Y positions for page-relative rendering
 *
 * Creates new line objects with adjusted baseline_y_fp values.
 * Does NOT mutate the input array.
 *
 * @param lines - Lines to adjust
 * @param pageYOffset - Y offset to subtract
 * @returns New array with adjusted lines
 */
export function adjustLineYPositions<T extends LineWithYPosition>(
  lines: T[],
  pageYOffset: number
): T[] {
  if (pageYOffset === 0 || lines.length === 0) {
    return lines;
  }

  return lines.map(line => ({
    ...line,
    baseline_y_fp: line.baseline_y_fp - pageYOffset
  }));
}

/**
 * Adjusts caret Y positions for page-relative rendering
 *
 * Creates new caret entry objects with adjusted y_fp values.
 * Does NOT mutate the input array.
 *
 * @param caretEntries - Caret entries to adjust
 * @param pageYOffset - Y offset to subtract
 * @returns New array with adjusted caret entries
 */
export function adjustCaretEntryYPositions<T extends CaretEntryWithYPosition>(
  caretEntries: T[],
  pageYOffset: number
): T[] {
  if (pageYOffset === 0 || caretEntries.length === 0) {
    return caretEntries;
  }

  return caretEntries.map(entry => ({
    ...entry,
    y_fp: (entry.y_fp ?? 0) - pageYOffset
  }));
}

/**
 * Options for adjusting page layout
 */
export interface AdjustPageLayoutOptions {
  /** Page index (0-based) */
  pageIndex: number;
  /** Page setup with margins */
  wasmPageSetup?: { margins_fp?: PageMarginsRef };
  /** Enable console logging */
  enableLogging?: boolean;
}

/**
 * Result of page layout adjustment
 */
export interface PageLayoutAdjustmentResult<
  TLine extends LineWithYPosition,
  TCaret extends CaretEntryWithYPosition
> {
  /** Adjusted lines */
  adjustedLines: TLine[];
  /** Adjusted caret map */
  adjustedCaretMap: TCaret[];
  /** Y offset that was applied */
  pageYOffset: number;
  /** Whether any adjustment was made */
  wasAdjusted: boolean;
}

/**
 * Adjusts both lines and caret map for page-relative rendering
 *
 * This is a convenience function that combines calculatePageYOffset,
 * adjustLineYPositions, and adjustCaretEntryYPositions into a single operation.
 *
 * @param lines - Lines on the current page
 * @param caretMap - Caret map entries for the page
 * @param options - Adjustment options
 * @returns Adjusted lines and caret map
 *
 * @example
 * ```typescript
 * const result = adjustPageLayout(pageLines, pageCaretMap, {
 *   pageIndex: 1,
 *   wasmPageSetup: wasmPageSetup,
 *   enableLogging: DEBUG_ENABLED
 * });
 * 
 * // Use result.adjustedLines and result.adjustedCaretMap
 * ```
 */
export function adjustPageLayout<
  TLine extends LineWithYPosition,
  TCaret extends CaretEntryWithYPosition
>(
  lines: TLine[],
  caretMap: TCaret[],
  options: AdjustPageLayoutOptions
): PageLayoutAdjustmentResult<TLine, TCaret> {
  const { pageIndex, wasmPageSetup, enableLogging = false } = options;
  
  // Calculate Y offset
  const topMarginFp = wasmPageSetup?.margins_fp?.top ?? 0;
  const offsetResult = calculatePageYOffset(lines, {
    pageIndex,
    topMarginFp,
    enableLogging: false // We'll do our own logging
  });

  if (!offsetResult.needsAdjustment) {
    return {
      adjustedLines: lines,
      adjustedCaretMap: caretMap,
      pageYOffset: 0,
      wasAdjusted: false
    };
  }

  const { pageYOffset } = offsetResult;

  // Adjust both lines and caret map
  const adjustedLines = adjustLineYPositions(lines, pageYOffset);
  const adjustedCaretMap = adjustCaretEntryYPositions(caretMap, pageYOffset);

  if (enableLogging) {
    console.log(`🎨 [PageLayoutCalculator] Page ${pageIndex}: Adjusted Y positions by ${pageYOffset / 64}px`, {
      originalFirstBaseline: offsetResult.originalFirstBaseline / 64,
      adjustedFirstBaseline: (offsetResult.originalFirstBaseline - pageYOffset) / 64,
      topMargin: topMarginFp / 64,
      lineCount: adjustedLines.length
    });
  }

  return {
    adjustedLines,
    adjustedCaretMap,
    pageYOffset,
    wasAdjusted: true
  };
}
